import React from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Grid,
    TextField,
    Tooltip,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import DateRangeSelect from '../common/DateRangeSelect/DateRangeSelect';
import MultiAutocomplete from '../common/MultiAutocomplete/MultiAutocomplete';
import { getFormattedCompanyNameSuggestions } from '../../services/autocompleteService';
import suggestionsPropType from '../../proptypes/suggestionsPropType';
import translationPropType from '../../proptypes/translationPropType';
import companyStatsFormStatePropTypes from '../../proptypes/companyStatsFormStatePropTypes';

const styles = theme => ({
    button: {
        marginRight: theme.spacing(2),
    },
});

const CompanyStatsForm = (props) => {
    const {
        classes,
        handleClear,
        handleSubmit,
        handleUpdateFormField,
        selectedCompanySuggestions,
        handleSelectCompanySuggestions,
        locale,
        searchActive,
        formState,
        translation,
    } = props;

    const setField = (field, value) => {
        handleUpdateFormField({ [field]: value });
    };

    const handleChangeEvent = (event) => {
        handleUpdateFormField({ [event.target.name]: event.target.value });
    };

    const handleCompanySelect = (selected) => {
        handleSelectCompanySuggestions(selected);
        handleUpdateFormField({ hid: selected.map(company => company.value).join(',') });
    };

    const isValidForm = () => (!!formState.hid && formState.hid.length >= 8)
            || (!!formState.orgNo && formState.orgNo.length >= 12);


    const submit = (event) => {
        if (event) {
            event.preventDefault();
        }
        if (isValidForm()) {
            handleSubmit({ ...formState });
        }
    };

    const hasSelectedCompanies = selectedCompanySuggestions.length > 0;
    const hasHidValue = formState.hid.length > 0;

    return (
        <form onSubmit={submit} autoComplete="off">
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <MultiAutocomplete
                        handleSelect={handleCompanySelect}
                        selectedSuggestions={selectedCompanySuggestions}
                        label={translation.COMPANY}
                        placeholder={translation.SEARCH_COMPANIES}
                        handleGetSuggestions={getFormattedCompanyNameSuggestions}
                        handleSubmit={submit}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Tooltip
                        title={hasSelectedCompanies
                            ? translation.TOOLTIP_HID_INPUT_DISABLED
                            : translation.TOOLTIP_HID_INPUT
                        }
                    >
                        <TextField
                            label={translation.HID}
                            value={formState.hid}
                            disabled={hasSelectedCompanies}
                            name="hid"
                            onChange={handleChangeEvent}
                            fullWidth
                            margin="dense"
                            variant="outlined"
                        />
                    </Tooltip>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Tooltip
                        title={hasHidValue
                            ? translation.TOOLTIP_ORGNO_INPUT_DISABLED
                            : translation.TOOLTIP_ORGNO_INPUT
                        }
                    >
                        <TextField
                            label={translation.ORG_NUMBER}
                            value={formState.orgNo}
                            disabled={hasHidValue}
                            name="orgNo"
                            onChange={handleChangeEvent}
                            fullWidth
                            margin="dense"
                            variant="outlined"
                        />
                    </Tooltip>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <DateRangeSelect
                        to={formState.to}
                        locale={locale}
                        from={formState.from}
                        setValue={setField}
                        translation={translation}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        type="submit"
                        size="large"
                        variant="contained"
                        color="primary"
                        disabled={!isValidForm()}
                        className={classes.button}
                    >
                        {translation.SEARCH}
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        color="default"
                        className={classes.button}
                        onClick={handleClear}
                        disabled={!searchActive}
                    >
                        {translation.CLEAR}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

CompanyStatsForm.propTypes = {
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    translation: translationPropType.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleUpdateFormField: PropTypes.func.isRequired,
    handleClear: PropTypes.func.isRequired,
    handleSelectCompanySuggestions: PropTypes.func.isRequired,
    selectedCompanySuggestions: suggestionsPropType.isRequired,
    searchActive: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
    formState: companyStatsFormStatePropTypes.isRequired,
};

export default withStyles(styles)(CompanyStatsForm);
